// Print a PDF straight from the current page: the blob is loaded into a hidden
// iframe and the browser's print dialog is opened on it, so the user does not
// have to open the PDF in a tab (or download it) and find the viewer's print button.

const FRAME_TEST_ID = 'pdf-print-frame';

let activeFrame = null;
let activeUrl = null;

// The frame has to stay in the DOM while the print dialog is open, so it is
// only removed when the next print starts.
const cleanupActiveFrame = () => {
  if (activeFrame) {
    activeFrame.remove();
    activeFrame = null;
  }
  if (activeUrl) {
    window.URL.revokeObjectURL(activeUrl);
    activeUrl = null;
  }
};

// Inline printing needs a built-in PDF viewer that can print from an iframe.
// Phones and tablets cannot do that reliably (Android has no inline viewer at
// all), and neither can a desktop browser set to download PDFs.
export const canPrintPdfInline = () => {
  if (typeof navigator !== 'undefined' && navigator.pdfViewerEnabled === false) return false;
  if (typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches) return false;
  return true;
};

// Returns 'printed' when the print dialog was requested on this page, or
// 'opened' when the PDF was opened in a new tab as a fallback.
export const printPdfBlob = (data) => {
  const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));

  if (!canPrintPdfInline()) {
    window.open(url, '_blank');
    return 'opened';
  }

  cleanupActiveFrame();

  const iframe = document.createElement('iframe');
  iframe.setAttribute('data-testid', FRAME_TEST_ID);
  iframe.setAttribute('aria-hidden', 'true');
  iframe.setAttribute('tabindex', '-1');
  // Not display:none — a frame that is not rendered prints as a blank page.
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.onload = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (err) {
      console.error('Inline PDF print failed, opening in a new tab', err);
      window.open(url, '_blank');
    }
  };
  iframe.src = url;
  document.body.appendChild(iframe);

  activeFrame = iframe;
  activeUrl = url;
  return 'printed';
};
